/**
 * Wiki Service
 *
 * Thin orchestrator over GoogleDriveService for the Knowledge Base wiki layer.
 * Manages wiki pages (entity, concept, synthesis) stored in Google Drive.
 * All AI synthesis goes through OpenRouter.
 */

import { nanoid } from 'nanoid';
import { googleDrive } from './googleDrive';
import { openRouter } from './openRouter';
import { getCurrentISODate } from '@/utils/dates';
import type {
  WikiPage,
  WikiPageKind,
  WikiIndex,
  WikiIndexEntry,
  WikiSyncResult,
  Item,
} from '@/types';

const BATCH_SIZE = 12;
const DEFAULT_INDEX: WikiIndex = { pages: [], lastSyncedAt: '', version: 1 };
const VALID_KINDS: WikiPageKind[] = ['entity', 'concept', 'synthesis'];

export type SyncProgressCallback = (current: number, total: number, itemTitle: string) => void;

/**
 * Validate that a wiki page has all required fields.
 */
function isValidPageShape(p: unknown): p is WikiPage {
  if (!p || typeof p !== 'object') return false;
  const obj = p as Record<string, unknown>;
  return (
    typeof obj.slug === 'string' &&
    obj.slug.length > 0 &&
    typeof obj.title === 'string' &&
    typeof obj.kind === 'string' &&
    VALID_KINDS.includes(obj.kind as WikiPageKind) &&
    typeof obj.content === 'string'
  );
}

class WikiService {
  /**
   * Read the wiki index from Drive.
   * Returns a default empty index if none exists yet.
   */
  async getIndex(): Promise<WikiIndex> {
    const wikiFolderId = await googleDrive.ensureWikiFolder();
    return googleDrive.readJson<WikiIndex>('index.json', DEFAULT_INDEX, wikiFolderId);
  }

  /**
   * Get all wiki pages (index entries for the Knowledge tab listing).
   */
  async getAllPages(): Promise<WikiIndexEntry[]> {
    const index = await this.getIndex();
    return index.pages;
  }

  /**
   * Read a single wiki page by slug.
   */
  async getPage(slug: string): Promise<WikiPage | null> {
    const pagesFolderId = await googleDrive.ensureWikiPagesFolder();
    const page = await googleDrive.readJson<WikiPage | null>(`${slug}.json`, null, pagesFolderId);
    return page;
  }

  /**
   * Get items that are backlinked from a wiki page.
   * Returns item IDs stored in the page's backlinks array.
   */
  async getBacklinkIds(slug: string): Promise<string[]> {
    const page = await this.getPage(slug);
    return page?.backlinks ?? [];
  }

  /**
   * Main sync operation. Processes all items since last sync in batches,
   * sends each batch to OpenRouter for entity/concept extraction,
   * and writes updated wiki pages to Drive.
   *
   * Failed batches are tracked so lastSyncedAt only advances past
   * successfully processed items, ensuring failed items are retried.
   */
  async syncKnowledge(
    allItems: Item[],
    onProgress?: SyncProgressCallback
  ): Promise<{ pagesCreated: number; pagesUpdated: number; pagesDeleted: number }> {
    const index = await this.getIndex();
    const wikiFolderId = await googleDrive.ensureWikiFolder();
    const pagesFolderId = await googleDrive.ensureWikiPagesFolder();

    // Filter items since last sync (first sync = all items).
    // Use >= so items at the exact lastSyncedAt boundary are included
    // (needed for failed-batch retry where lastSyncedAt = oldest failed item's time)
    const itemsToProcess = index.lastSyncedAt
      ? allItems.filter((item) => item.updatedAt >= index.lastSyncedAt)
      : allItems;

    if (itemsToProcess.length === 0) {
      const updatedIndex: WikiIndex = {
        ...index,
        lastSyncedAt: getCurrentISODate(),
      };
      await googleDrive.writeJsonImmediate('index.json', updatedIndex, wikiFolderId);
      return { pagesCreated: 0, pagesUpdated: 0, pagesDeleted: 0 };
    }

    // Chunk items into batches
    const batches: Item[][] = [];
    for (let i = 0; i < itemsToProcess.length; i += BATCH_SIZE) {
      batches.push(itemsToProcess.slice(i, i + BATCH_SIZE));
    }

    // Track known pages across batches so later batches see earlier creates
    const knownPages: WikiIndexEntry[] = [...(index.pages ?? [])];

    let totalCreated = 0;
    let totalUpdated = 0;
    let totalDeleted = 0;
    let processedCount = 0;
    let hasFailedBatch = false;
    let oldestFailedItemTime: string | null = null;

    for (const batch of batches) {
      if (onProgress) {
        const firstItem = batch[0];
        onProgress(processedCount + 1, itemsToProcess.length, firstItem?.title ?? 'Processing...');
      }

      try {
        const result = await this.synthesizeBatch(batch, knownPages, pagesFolderId);

        // Write new pages (with schema validation)
        for (const page of result.create) {
          if (!isValidPageShape(page)) {
            console.warn('Skipping malformed create page:', page);
            continue;
          }
          const fullPage: WikiPage = {
            id: nanoid(10),
            slug: page.slug,
            title: page.title,
            kind: page.kind,
            content: page.content,
            backlinks: Array.isArray(page.backlinks) ? page.backlinks : [],
            relatedPages: Array.isArray(page.relatedPages) ? page.relatedPages : [],
            itemCount: typeof page.itemCount === 'number' ? page.itemCount : 0,
            createdAt: getCurrentISODate(),
            updatedAt: getCurrentISODate(),
            lastSyncedAt: getCurrentISODate(),
          };
          await googleDrive.writeJsonImmediate(`${fullPage.slug}.json`, fullPage, pagesFolderId);
          // Track this new page for later batches
          knownPages.push({
            slug: fullPage.slug,
            title: fullPage.title,
            kind: fullPage.kind,
            itemCount: fullPage.itemCount,
            updatedAt: fullPage.updatedAt,
          });
          totalCreated++;
        }

        // Update existing pages (merge, don't overwrite)
        for (const update of result.update) {
          if (!update.slug || typeof update.slug !== 'string') continue;
          const existing = await this.getPage(update.slug);
          if (existing) {
            // Union backlinks rather than replacing
            const mergedBacklinks = [
              ...new Set([
                ...existing.backlinks,
                ...(Array.isArray(update.backlinks) ? update.backlinks : []),
              ]),
            ];
            const merged: WikiPage = {
              ...existing,
              content: typeof update.content === 'string' ? update.content : existing.content,
              backlinks: mergedBacklinks,
              relatedPages: Array.isArray(update.relatedPages)
                ? [...new Set([...existing.relatedPages, ...update.relatedPages])]
                : existing.relatedPages,
              itemCount:
                typeof update.itemCount === 'number'
                  ? Math.max(existing.itemCount, update.itemCount)
                  : existing.itemCount,
              updatedAt: getCurrentISODate(),
              lastSyncedAt: getCurrentISODate(),
            };
            await googleDrive.writeJsonImmediate(`${merged.slug}.json`, merged, pagesFolderId);
            totalUpdated++;
          }
        }

        totalDeleted += result.delete.length;
      } catch (error) {
        console.warn('Wiki sync batch failed, skipping batch:', error);
        hasFailedBatch = true;
        // Track the oldest item in the failed batch so we don't advance past it
        const oldestInBatch = batch.reduce((oldest, item) =>
          item.updatedAt < oldest.updatedAt ? item : oldest
        );
        if (!oldestFailedItemTime || oldestInBatch.updatedAt < oldestFailedItemTime) {
          oldestFailedItemTime = oldestInBatch.updatedAt;
        }
      }

      processedCount += batch.length;
    }

    // Report final progress
    if (onProgress) {
      onProgress(processedCount, itemsToProcess.length, 'Complete');
    }

    // Rebuild and write index
    const allPageFiles = await googleDrive.listFilesInFolder(pagesFolderId);
    const newPages: WikiIndexEntry[] = [];

    for (const file of allPageFiles) {
      const page = await googleDrive.readJson<WikiPage | null>(file.name, null, pagesFolderId);
      if (page) {
        newPages.push({
          slug: page.slug,
          title: page.title,
          kind: page.kind,
          itemCount: page.itemCount,
          updatedAt: page.updatedAt,
        });
      }
    }

    // Only advance lastSyncedAt to just before the oldest failed item,
    // so failed items will be retried on the next sync
    const syncTimestamp =
      hasFailedBatch && oldestFailedItemTime ? oldestFailedItemTime : getCurrentISODate();

    const updatedIndex: WikiIndex = {
      pages: newPages,
      lastSyncedAt: syncTimestamp,
      version: 1,
    };
    await googleDrive.writeJsonImmediate('index.json', updatedIndex, wikiFolderId);

    return { pagesCreated: totalCreated, pagesUpdated: totalUpdated, pagesDeleted: totalDeleted };
  }

  /**
   * Send a batch of items to OpenRouter for wiki synthesis.
   * Returns structured create/update/delete instructions.
   *
   * For updates, includes existing page content so the AI can make
   * informed incremental changes rather than blind overwrites.
   */
  private async synthesizeBatch(
    items: Item[],
    knownPages: WikiIndexEntry[],
    pagesFolderId: string
  ): Promise<WikiSyncResult> {
    const itemSummaries = items.map((item) => ({
      id: item.id,
      type: item.type,
      title: item.title,
      description: item.description,
      tags: item.tags,
      createdAt: item.createdAt,
      url: 'url' in item ? (item as { url: string }).url : undefined,
      content: 'content' in item ? (item as { content: string }).content : undefined,
    }));

    // For updates, include full existing page content so the AI can merge properly.
    // Limit to 20 pages to stay within token budget. Full content is needed
    // so the AI doesn't erase unseen parts when generating replacement content.
    const existingPagesContext = await Promise.all(
      knownPages.slice(0, 20).map(async (p) => {
        const page = await googleDrive.readJson<WikiPage | null>(
          `${p.slug}.json`,
          null,
          pagesFolderId
        );
        if (!page) return { slug: p.slug, title: p.title, kind: p.kind };
        return {
          slug: page.slug,
          title: page.title,
          kind: page.kind,
          currentContent: page.content,
          currentBacklinks: page.backlinks,
          itemCount: page.itemCount,
        };
      })
    );

    const prompt = `You are a knowledge base wiki maintainer. Analyze these personal items and determine which wiki pages to create or update.

EXISTING WIKI PAGES (with current content summaries):
${JSON.stringify(existingPagesContext)}

NEW ITEMS TO PROCESS:
${JSON.stringify(itemSummaries, null, 2)}

RULES:
- Entity pages: slug "openai", "nvda" (lowercase, hyphens). Titles "@OpenAI", "$NVDA".
- Concept pages: slug "ai-pricing-trends", "gpu-supply-chain".
- Synthesis pages: summarize themes across entities/concepts.
- Only create if 2+ items about the same entity/concept.
- For updates: provide the COMPLETE new content (incorporating existing content + new info). Include ALL backlink IDs (existing + new).
- backlinks = item IDs from the items above that reference this page.

Return ONLY valid JSON:
{
  "create": [{ "slug": "...", "title": "...", "kind": "entity|concept|synthesis", "content": "markdown...", "backlinks": ["id1"], "relatedPages": ["slug"], "itemCount": 3 }],
  "update": [{ "slug": "existing-slug", "content": "complete updated markdown...", "backlinks": ["all-item-ids"], "relatedPages": ["slugs"], "itemCount": 5 }],
  "delete": []
}`;

    const response = await openRouter.chat([{ role: 'user', content: prompt }], {
      maxTokens: 4000,
      temperature: 0.3,
    });

    const content = response.trim();
    const jsonStr = content.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');

    const result = JSON.parse(jsonStr) as WikiSyncResult;
    if (!Array.isArray(result.create)) result.create = [];
    if (!Array.isArray(result.update)) result.update = [];
    if (!Array.isArray(result.delete)) result.delete = [];
    return result;
  }
}

export const wikiService = new WikiService();
